const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
const REQUEST_TIMEOUT_MS = 30 * 1000;

type RestRequestInit = Omit<NonNullable<Parameters<typeof fetch>[1]>, 'body'> & { body?: unknown };

export class RestError extends Error {
  response: { status: number; data: unknown };

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'RestError';
    this.response = { status, data };
  }
}

const readBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const readErrorMessage = (data: unknown, response: Response): string => {
  if (typeof data === 'string' && data) return data;
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.error === 'string' && body.error) return body.error;
  }
  return response.statusText || `Request failed with status code ${response.status}`;
};

const timeoutSignal = (): AbortSignal | undefined => (
  typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined
);

export const restRequest = async <T = unknown>(
  path: string,
  init: RestRequestInit = {},
): Promise<T> => {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${API_BASE_URL}/${path.replace(/^\/+/, '')}`, {
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    signal: init.signal ?? timeoutSignal(),
  });

  const data = await readBody(response);
  if (!response.ok) throw new RestError(readErrorMessage(data, response), response.status, data);
  return data as T;
};
